<script lang="ts">
  /**
   * A one-line notice at the bottom of the screen that goes away on its own —
   * "that happened", after an action that closed the sheet it was taken in.
   *
   * A polite live region, so a screen reader reads it without moving focus.
   * The caller keys it (`{#key}`) to show the same words twice in a row.
   *
   * **The region is inserted empty and the words arrive a beat later.** Many
   * screen readers announce a live region's *changes*, not a region that
   * appears with its text already inside — so a snackbar mounted with its
   * message said nothing to exactly the people it was for (review finding).
   * The pill itself is drawn only once there are words to put in it.
   *
   * `detail` (since 1.5.0) is an optional second, quieter line under the
   * message ("Robin played 31" over "with a bonus"); both arrive together
   * and are read as one announcement. With a second line the pill becomes a
   * rounded card (`--ui-radius-lg`), so two lines never sit in a stadium.
   *
   * `action` (since 1.7.0): one word that can be pressed ("Open" on a chat
   * message), a real `<button>` at the pill's end. Its timing respects it:
   *
   * - it stays longer (`durationMs` defaults to 8 s with an action, 2.6 s
   *   without), and
   * - it does not go while the pointer is over it or focus is in it; the
   *   time left resumes once both have left (never less than a second).
   *
   * Pressing it calls `action.onclick`, then `ondone` (the notice goes).
   * Focus is never left on a button that is going away: if `onclick` did not
   * move it (opening a sheet does), it goes back to the element that had it
   * when the notice appeared. If that element is gone by then (or nothing
   * had focus), `onclick` must move focus itself, to what it opened. The
   * button sits beside the live region, not in it, so a screen reader
   * announces the message alone, never the button's word. The notice still
   * goes by itself, so an action is a shortcut to something the game also
   * offers elsewhere (the chat button), never the only way to it.
   *
   * **It clears the frame's navigation** (since 1.8.0): over an `AppFrame`
   * that draws the tab bar it sits above the bar, and beside the rail or the
   * sidebar it is centred in what they leave. The frame says how much its navigation
   * takes (`--ui-frame-bottom`, `--ui-frame-left`, set on the root while it
   * draws them); the notice reads them and never measures the page. With
   * no frame (over a game) it sits where it always did.
   *
   * Test ids: `testid` on the live region; `action.testid` on the button. No
   * defaults.
   */
  import { onMount } from 'svelte';

  import type { SnackbarAction } from '../types.js';

  interface Props {
    readonly message: string;
    /** A second, quieter line under the message (since 1.5.0). */
    readonly detail?: string | undefined;
    /** Called once the notice has been on screen for `durationMs`, or its action was pressed. */
    readonly ondone: () => void;
    /** How long it stays: 2600 by default, 8000 with an `action`. */
    readonly durationMs?: number | undefined;
    /**
     * How long the empty region stands in the page before the words go in —
     * long enough for assistive technology to register the region first.
     */
    readonly announceDelayMs?: number;
    /** One word that can be pressed, at the pill's end (since 1.7.0). */
    readonly action?: SnackbarAction | undefined;
    readonly testid?: string | undefined;
  }
  const {
    message,
    detail,
    ondone,
    durationMs,
    announceDelayMs = 100,
    action,
    testid,
  }: Props = $props();

  /** The least time left once the pointer or focus leaves (1.7.0). */
  const RESUME_MIN_MS = 1000;

  let shown = $state('');
  let shownDetail = $state('');

  // Read once, at mount: the notice is keyed by its caller, never re-timed.
  // svelte-ignore state_referenced_locally
  const lasts = durationMs ?? (action === undefined ? 2600 : 8000);

  let timer: number | undefined;
  let endsAt = 0;
  let left = lasts;
  let done = false;
  let hovered = false;
  let focused = false;
  /** The pill with the action (1.7.0), and what had focus when the notice appeared. */
  let bar = $state<HTMLDivElement | undefined>();
  let before: HTMLElement | null = null;

  function finish(): void {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    ondone();
  }

  function run(ms: number): void {
    window.clearTimeout(timer);
    endsAt = Date.now() + ms;
    timer = window.setTimeout(finish, ms);
  }

  /** Held while the pointer is over it or focus is in it; resumed when both have left. */
  function hold(next: { hovered?: boolean; focused?: boolean }): void {
    if (done) return;
    const wasHeld = hovered || focused;
    hovered = next.hovered ?? hovered;
    focused = next.focused ?? focused;
    const held = hovered || focused;
    if (held && !wasHeld) {
      left = Math.max(0, endsAt - Date.now());
      window.clearTimeout(timer);
    } else if (!held && wasHeld) {
      run(Math.max(left, RESUME_MIN_MS));
    }
  }

  function press(): void {
    if (done) return;
    action?.onclick();
    // Still on the button, which is about to go: back where it was.
    const active = document.activeElement;
    if (bar !== undefined && active instanceof Node && bar.contains(active)) {
      if (before !== null && before.isConnected) before.focus({ preventScroll: true });
    }
    finish();
  }

  onMount(() => {
    const active = document.activeElement;
    before = active instanceof HTMLElement && active !== document.body ? active : null;
    const announce = window.setTimeout(() => {
      shown = message;
      shownDetail = detail ?? '';
    }, announceDelayMs);
    run(lasts);
    return () => {
      done = true;
      window.clearTimeout(announce);
      window.clearTimeout(timer);
    };
  });
</script>

{#if action === undefined}
  <div class="ui-snackbar-region" role="status" aria-live="polite" data-testid={testid}>
    {#if shown !== ''}
      {#if shownDetail === ''}
        <span class="ui-snackbar">{shown}</span>
      {:else}
        <span class="ui-snackbar ui-snackbar--two"
          ><span class="ui-snackbar-line">{shown}</span>
          <span class="ui-snackbar-detail">{shownDetail}</span></span
        >
      {/if}
    {/if}
  </div>
{:else}
  <!-- With an action (1.7.0): the live region holds the words only; the
       button is its sibling, so it is never part of the announcement. -->
  <div class="ui-snackbar-region ui-snackbar-region--action">
    <div
      class="ui-snackbar-bar"
      bind:this={bar}
      class:ui-snackbar-bar--shown={shown !== ''}
      class:ui-snackbar-bar--two={shownDetail !== ''}
      role="presentation"
      onpointerenter={() => hold({ hovered: true })}
      onpointerleave={() => hold({ hovered: false })}
      onfocusin={() => hold({ focused: true })}
      onfocusout={(event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node && event.currentTarget.contains(next))) {
          hold({ focused: false });
        }
      }}
    >
      <span class="ui-snackbar-says" role="status" aria-live="polite" data-testid={testid}>
        {#if shown !== ''}<span class="ui-snackbar-line">{shown}</span>{#if shownDetail !== ''}
            <span class="ui-snackbar-detail">{shownDetail}</span>{/if}{/if}
      </span>
      {#if shown !== ''}
        <button type="button" class="ui-snackbar-action" data-testid={action.testid} onclick={press}
          >{action.label}</button
        >
      {/if}
    </div>
  </div>
{/if}

<style>
  /* The live region: always in the page while the notice is, never drawn. */
  /* It clears the frame's navigation (1.8.0): `AppFrame` says how much the
     tab bar takes at the bottom (`--ui-frame-bottom`) and the rail at the
     left (`--ui-frame-left`); without a frame both are absent and the notice
     sits where it always did. Read here, never measured. */
  .ui-snackbar-region {
    position: fixed;
    left: calc(50% + var(--ui-frame-left, 0px) / 2);
    bottom: calc(
      max(var(--ui-space-6), var(--ui-frame-bottom, 0px) + var(--ui-space-3)) +
        env(safe-area-inset-bottom)
    );
    transform: translateX(-50%);
    z-index: 30;
    max-width: calc(100% - var(--ui-frame-left, 0px) - 2 * var(--ui-space-4));
    pointer-events: none;
  }

  .ui-snackbar {
    display: block;
    padding: var(--ui-space-3) var(--ui-space-5);
    border-radius: 999px;
    background: var(--ui-ink);
    color: var(--ui-surface);
    font: var(--ui-text-label);
    font-weight: 800;
    box-shadow: var(--ui-shadow-float);
  }

  /* Two lines (since 1.5.0): a rounded card, the second line quieter. */
  .ui-snackbar--two {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    border-radius: var(--ui-radius-lg);
    text-align: center;
  }

  .ui-snackbar-line {
    display: block;
  }

  .ui-snackbar-detail {
    display: block;
    font: var(--ui-text-caption);
    font-weight: 400;
  }

  @media (prefers-reduced-motion: no-preference) {
    .ui-snackbar,
    .ui-snackbar-bar--shown {
      animation: ui-snackbar-in 0.18s ease-out;
    }
  }

  @keyframes ui-snackbar-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  /* With an action (1.7.0): the same pill, the words then the button; drawn
     only once the words are in, and the only part that takes a press. As
     wide as its words up to the screen's width less the gutters (a region
     placed from the middle would otherwise wrap at half the screen). */
  .ui-snackbar-region--action {
    width: max-content;
  }

  .ui-snackbar-bar {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
  }

  .ui-snackbar-bar--shown {
    padding: var(--ui-space-1) var(--ui-space-1) var(--ui-space-1) var(--ui-space-5);
    border-radius: 999px;
    background: var(--ui-ink);
    color: var(--ui-surface);
    font: var(--ui-text-label);
    font-weight: 800;
    box-shadow: var(--ui-shadow-float);
    pointer-events: auto;
  }

  .ui-snackbar-bar--two {
    border-radius: var(--ui-radius-lg);
  }

  .ui-snackbar-says {
    flex: 1 1 auto;
    min-width: 0;
    padding: var(--ui-space-2) 0;
  }

  /* The word on the same pair as the message (surface on ink), on a pill of
     its own so it reads as a button; the target reaches 2.75rem. */
  .ui-snackbar-action {
    position: relative;
    flex: 0 0 auto;
    min-height: 2.25rem;
    padding: 0.35rem 0.9rem;
    border: 2px solid var(--ui-surface);
    border-radius: 999px;
    background: transparent;
    color: var(--ui-surface);
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .ui-snackbar-action::before {
    content: '';
    position: absolute;
    inset: -0.25rem 0;
  }

  /* The ring in the words' colour: surface on ink is the pair the notice is
     already gated on (the accent on ink is not). */
  .ui-snackbar-action:focus-visible {
    outline: 3px solid var(--ui-surface);
    /* Inside the pill's 0.25rem end padding, so the ring never meets the page. */
    outline-offset: 1px;
  }
</style>
